import type { Tone } from "../../../components/ui";
import type { AlertStatus, ConnectionStatus, Money, Operation, OperationStage, OperationStatus, Severity } from "../services/radarCorreoService";

/** Fecha corta local (o "—"). Acepta ms, ISO o Timestamp serializado de Firestore. */
export function fmtDate(value: unknown, locale = "es-MX"): string {
  const ms = toMs(value);
  if (!ms) return "—";
  return new Date(ms).toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" });
}

export function fmtDateTime(value: unknown, locale = "es-MX"): string {
  const ms = toMs(value);
  if (!ms) return "—";
  return new Date(ms).toLocaleString(locale, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export function toMs(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const t = Date.parse(value);
    return Number.isFinite(t) ? t : null;
  }
  const v = value as { _seconds?: number; seconds?: number };
  if (typeof v._seconds === "number") return v._seconds * 1000;
  if (typeof v.seconds === "number") return v.seconds * 1000;
  return null;
}

export function fmtMoney(amount: number, currency: string, locale = "es-MX"): string {
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency, maximumFractionDigits: 0 }).format(amount);
  } catch {
    return `${Math.round(amount).toLocaleString(locale)} ${currency}`;
  }
}

/** "MXN 1,200 · USD 300" ordenado de mayor a menor. */
export function fmtMoneyMap(m: Money | null | undefined, locale = "es-MX"): string {
  const entries = Object.entries(m || {}).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);
  if (!entries.length) return "—";
  return entries.map(([c, v]) => fmtMoney(v, c, locale)).join(" · ");
}

export function fmtHours(h: number | null | undefined): string {
  if (h == null) return "—";
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 48) return `${Math.round(h * 10) / 10} h`;
  return `${Math.round((h / 24) * 10) / 10} d`;
}

export function fmtPct(p: number | null | undefined): string {
  return p == null ? "—" : `${Math.round(p)} %`;
}

export const STAGE_TONE: Record<OperationStage, Tone> = {
  quote: "neutral",
  booked: "info",
  in_transit: "primary",
  delivered: "success",
  invoiced: "warning",
  paid: "success",
  closed: "neutral",
};

export const OP_STATUS_TONE: Record<OperationStatus, Tone> = {
  proposed: "warning",
  confirmed: "success",
  dismissed: "neutral",
  merged: "neutral",
};

export const SEVERITY_TONE: Record<Severity, Tone> = {
  low: "neutral",
  medium: "info",
  high: "warning",
  critical: "error",
};

export const ALERT_STATUS_TONE: Record<AlertStatus, Tone> = {
  open: "error",
  ack: "info",
  snoozed: "neutral",
  resolved: "success",
};

export const CONNECTION_TONE: Record<ConnectionStatus, Tone> = {
  pending: "neutral",
  active: "success",
  paused: "warning",
  error: "error",
  revoked: "neutral",
  budget_exceeded: "warning",
  consent_required: "warning",
};

export const PROVIDER_ICON: Record<string, string> = {
  gmail: "ri-google-line",
  outlook: "ri-microsoft-line",
  imap: "ri-mail-settings-line",
};

/** La referencia más representativa de una operación. */
export function primaryRef(op: Pick<Operation, "refs">): string | null {
  const r = op.refs || {};
  return r.bl?.[0] || r.awb?.[0] || r.container?.[0] || r.booking?.[0] || r.tracking?.[0] || r.pedimento?.[0] || null;
}

export function laneLabel(op: Pick<Operation, "lane">): string {
  const l = op.lane;
  if (!l || (!l.origin && !l.destination)) return "—";
  return `${l.origin || "?"} → ${l.destination || "?"}`;
}
